import BlogPage from 'src/views/Marketing/Blog/components/BlogPage';
import Footer from 'src/views/Marketing/components/Footer';
import NavBar from 'src/views/Marketing/components/NavBar';
import { ScrollView, StyleSheet } from 'react-native';
import * as Constants from 'src/constants';

export default function Page() {
  return (
    <ScrollView style={styles.scrollView}>
      <NavBar />
      <BlogPage
        author='OneShop Team'
        dateTime='2022-04-29'
        tag='Grailed'
        title='Grailed photo tips: Guidelines and checklist to follow for successful listings'
      >
        <p>
          Grailed sets itself apart from other community marketplaces for its selection of mostly high-end brands and
          designer pieces. This elite branding is evident in their website's clean aesthetics and the photo quality of
          the listed items on their platform.
        </p>
        <p>
          Maintaining a uniform look and feel for the photos reinforces Grailed's branding. But more importantly,
          requiring sellers to provide specific photos helps establish Grailed's reputation as a legitimate, reliable
          source of new or used premium clothing, shoes, and accessories and helps boost the sellers' trustworthiness as
          well.
        </p>
        <p>
          To ensure that the photos uploaded by the sellers meet their specifications, moderators check the listings and
          may request additional photos or, better, clearer shots. Knowing the requirements beforehand will save you and
          the moderators time and effort. Below is a cheat sheet to help you get it right every time you list.
        </p>
        <p>
          But before we get into the specifics, remember the main factors that affect the quality of your photo. These
          factors are applicable not just on Grailed but on other platforms, too.
        </p>
        <ul>
          <li>
            <strong>Camera.</strong> A professional camera is ideal, but since not everyone has one, make do with your
            phone camera and optimize the quality with good lighting and a clean background.
          </li>
          <li>
            <strong>Lighting.</strong> Natural, ambient lighting doesn't cost anything, but it can make your items look
            like a million bucks. Make sure to hold your product shoot in the daytime and do it with an open window or
            door nearby. Relying on sunlight instead of an artificial source (e.g., flash function or any lighting
            fixture) puts your items in a better light and will not leave an unflattering shadow or weird cast. The
            natural color and grain or fabric will be more visible, giving potential buyers an honest view of your
            items.
          </li>
          <li>
            <strong>Background.</strong> There's no need to set up a studio area for this. All you need is a blank white
            or neutral-colored wall with a hook to hang the article of clothing you're listing. If you're listing small
            items, take photos of them on a clean, uncluttered table or floor.
          </li>
        </ul>
        <p>
          When these factors are present, it would be much easier to achieve the bright, clear, neat images required by
          Grailed. Do light photo enhancements if you need to make the images sharper, but don't overdo it. You would
          want the photos to be as accurate a representation as possible of the actual items.
        </p>

        <h3>YOUR GRAILED LISTING PHOTO CHECKLIST</h3>
        <p>Now that you've set the stage for taking great shots, make sure you cover these photo requirements:</p>
        <ul>
          <li>
            <strong>At least three photos.</strong> Include at least three photos of the items showing different angles,
            highlighting details <em>(refer to the examples below)</em>, and revealing any imperfections.{' '}
          </li>
          <li>
            <strong>Brand tag.</strong> Take a photo of the brand or designer label on the tag (neck, sleeve, or other
            areas).
          </li>
          <li>
            <strong>Wash or care tag.</strong> Also, include a clear close-up photo of the tag or tags bearing the
            instructions for washing and caring for the item.
          </li>
          <li>
            <strong>Branding details.</strong> Features distinct to the brand or designer label can be seen on the
            hardware (e.g., buttons, zippers, buckles) or details such as a stamp, a badge, or a patch that bears the
            logo. Take clear close-up shots of these.
          </li>
          <li>
            <strong>Proof of authenticity.</strong> A luxury item usually comes with a serial number to establish it as
            a genuine article – include a clear close-up shot of it as well. Suppose you want your item to be
            authenticated by Grailed. In that case, you will have to provide them with the receipt of the item upon its
            purchase or any legal documentation that proves the genuineness of the item you would like to be
            authenticated and listed.
          </li>
          <li>
            <strong>Additional shots for sneakers listings.</strong> If you're selling sneakers, be prepared to take
            more photos. Doing so helps capture details such as the sneaker tongue (both front and the back where the
            labels are), insole and outsole (inner and bottom soles of sneakers), shoelaces (taken from above, laces in
            place), a shoebox (it is a must to include a photo of the manufacturer's label). Don't forget to check and
            take a photo of the insole for any tags you might have missed and include them in the photos.
          </li>
          <li>
            <strong>Tagged photo.</strong> Grailed is very particular about including tagged photos in the listings,
            especially if it's of high value. This is an added layer of protective measure to ensure that the item is
            yours and you have it on hand to ship (as opposed to being stolen photos from another seller or website).
            This gives potential buyers the confidence to do business with you.
            <p>
              To produce a Grailed tagged photo, simply write down your Grailed username on paper. Below it, write the
              current date and then the word "Grailed. Place this piece of paper beside the item you're listing and take
              a photo of it.
            </p>
          </li>
        </ul>

        <p>
          These photo requirements may seem intimidating at first; hence you need a checklist to guide you. The goal is
          to submit complete and high-quality photos so you won't have to spend additional time and effort to re-shoot
          and re-submit the images.
        </p>
        <p>
          As a community marketplace seller, there are other ways to be more efficient in running your reselling
          business, especially if you also sell on Poshmark, Mercari, or other platforms.{' '}
        </p>
        <p>
          Consider OneShop, an all-in-one -app that has proven to help thousands of resellers. Aside from conveniently
          automating your closet, it also offers helpful features that aid sellers like you with tracking sales, setting
          goals, and having a supportive seller community.
        </p>
        <p>
          <a href='https://tools.oneshop.com/signup'>Sign-up to OneShop</a> and take advantage of its 7-day free trial
          with no outright payment. Let this excellent automation service help you have more time on your hands to focus
          on other things essential to growing your business.{' '}
        </p>
      </BlogPage>
      <Footer />
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
});
